import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Typography, Button, IconButton, TextField, MenuItem, Grid,
  Drawer, Divider, FormControlLabel, Switch, InputAdornment, Tooltip, useTheme,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useLocation, useNavigate } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SearchIcon from '@mui/icons-material/Search';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import EmailIcon from '@mui/icons-material/Email';
import PlaceIcon from '@mui/icons-material/Place';
import PublicIcon from '@mui/icons-material/Public';
import BadgeIcon from '@mui/icons-material/Badge';

import Header from '../../components/Header';
import { supabase } from '../../lib/supabaseClient';

const dt = (v) => (v ? new Date(v).toLocaleString() : '—');
const digitsOnly = (s) => (s || '').replace(/[^\d]/g, '');

function AddProviderDialog({ open, onClose, onCreated, orgId, facilities }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    npi: '', first_name: '', last_name: '', email: '', phone: '', fax: '',
    address: '', city: '', state: '', zip: '', country: 'US', facility_id: '', include_medsmatrix: false,
  });

  useEffect(() => {
    if (open) {
      setForm({
        npi: '', first_name: '', last_name: '', email: '', phone: '', fax: '',
        address: '', city: '', state: '', zip: '', country: 'US',
        facility_id: facilities?.[0]?.id || '', include_medsmatrix: false,
      });
      setSaving(false);
    }
  }, [open, facilities]);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    if (!orgId) return alert('Select an organization first.');
    if (!form.npi || !form.last_name || !form.first_name) {
      alert('NPI, First Name, and Last Name are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        org_id: orgId,
        facility_id: form.facility_id || null,
        npi: String(form.npi).trim(),
        first_name: form.first_name || null,
        last_name: form.last_name || null,
        email: form.email || null,
        phone: digitsOnly(form.phone) || null,
        fax: digitsOnly(form.fax) || null,
        address: form.address || null,
        city: form.city || null,
        state: form.state || null,
        zip: form.zip || null,
        country: form.country || null,
        include_medsmatrix: !!form.include_medsmatrix,
      };

      const { error } = await supabase.from('providers').insert([payload]);
      if (error) throw error;

      onCreated?.();
      onClose();
    } catch (e) {
      alert(`Failed to add provider: ${e.message}`);
      setSaving(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 440 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
        <Typography variant="h6">Add Provider</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box p={2} display="grid" gap={2}>
        <TextField
          label="NPI *" value={form.npi} onChange={set('npi')}
          InputProps={{ startAdornment: (<InputAdornment position="start"><BadgeIcon /></InputAdornment>) }}
        />
        <Grid container spacing={1}>
          <Grid item xs={6}><TextField label="First Name *" value={form.first_name} onChange={set('first_name')} /></Grid>
          <Grid item xs={6}><TextField label="Last Name *" value={form.last_name} onChange={set('last_name')} /></Grid>
        </Grid>
        <TextField
          label="Email" value={form.email} onChange={set('email')}
          InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon /></InputAdornment>) }}
        />
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <TextField
              label="Phone" value={form.phone} onChange={set('phone')} placeholder="(###) ###-####"
              InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneIphoneIcon /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={6}><TextField label="Fax" value={form.fax} onChange={set('fax')} /></Grid>
        </Grid>

        <TextField label="Address" value={form.address} onChange={set('address')} />
        <Grid container spacing={1}>
          <Grid item xs={5}><TextField label="City" value={form.city} onChange={set('city')} /></Grid>
          <Grid item xs={3}><TextField label="State" value={form.state} onChange={set('state')} /></Grid>
          <Grid item xs={4}><TextField label="ZIP" value={form.zip} onChange={set('zip')} /></Grid>
        </Grid>
        <TextField
          label="Country" value={form.country} onChange={set('country')}
          InputProps={{ startAdornment: (<InputAdornment position="start"><PublicIcon /></InputAdornment>) }}
        />

        <TextField select label="Facility" value={form.facility_id} onChange={set('facility_id')} helperText="Assign the provider to a facility">
          {(facilities || []).map((f) => (<MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>))}
        </TextField>

        <FormControlLabel control={<Switch checked={form.include_medsmatrix} onChange={set('include_medsmatrix')} />} label="Include MedsMatrix on Reports" />
      </Box>
      <Divider />
      <Box p={2} display="flex" gap={1}>
        <Button onClick={onClose} fullWidth>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} fullWidth disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Drawer>
  );
}

function EditProviderDrawer({ open, provider, onClose, onSaved, onDeleted, facilities }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(provider || {});
  useEffect(() => { setForm(provider || {}); setSaving(false); }, [provider, open]);
  if (!provider) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        first_name: form.first_name ?? null,
        last_name: form.last_name ?? null,
        email: form.email ?? null,
        phone: digitsOnly(form.phone) || null,
        fax: digitsOnly(form.fax) || null,
        address: form.address ?? null,
        city: form.city ?? null,
        state: form.state ?? null,
        zip: form.zip ?? null,
        country: form.country ?? null,
        include_medsmatrix: !!form.include_medsmatrix,
        facility_id: form.facility_id || null,
      };
      const { error } = await supabase.from('providers').update(payload).eq('id', provider.id);
      if (error) throw error;
      onSaved?.(); onClose();
    } catch (e) {
      alert(`Update failed: ${e.message}`); setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this provider? This cannot be undone.')) return;
    try {
      const { error } = await supabase.from('providers').delete().eq('id', provider.id);
      if (error) throw error;
      onDeleted?.(); onClose();
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 440 } }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" p={2}>
        <Typography variant="h6">Edit Provider</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>
      <Divider />
      <Box p={2} display="grid" gap={2}>
        <TextField label="NPI" value={provider.npi || ''} disabled />
        <Grid container spacing={1}>
          <Grid item xs={6}><TextField label="First Name" value={form.first_name || ''} onChange={set('first_name')} /></Grid>
          <Grid item xs={6}><TextField label="Last Name" value={form.last_name || ''} onChange={set('last_name')} /></Grid>
        </Grid>
        <TextField label="Email" value={form.email || ''} onChange={set('email')} InputProps={{ startAdornment: (<InputAdornment position="start"><EmailIcon /></InputAdornment>) }} />
        <Grid container spacing={1}>
          <Grid item xs={6}><TextField label="Phone" value={form.phone || ''} onChange={set('phone')} InputProps={{ startAdornment: (<InputAdornment position="start"><PhoneIphoneIcon /></InputAdornment>) }} /></Grid>
          <Grid item xs={6}><TextField label="Fax" value={form.fax || ''} onChange={set('fax')} /></Grid>
        </Grid>
        <TextField label="Address" value={form.address || ''} onChange={set('address')} />
        <Grid container spacing={1}>
          <Grid item xs={5}><TextField label="City" value={form.city || ''} onChange={set('city')} /></Grid>
          <Grid item xs={3}><TextField label="State" value={form.state || ''} onChange={set('state')} /></Grid>
          <Grid item xs={4}><TextField label="ZIP" value={form.zip || ''} onChange={set('zip')} /></Grid>
        </Grid>
        <TextField label="Country" value={form.country || 'US'} onChange={set('country')} InputProps={{ startAdornment: (<InputAdornment position="start"><PublicIcon /></InputAdornment>) }} />
        <TextField select label="Facility" value={form.facility_id || ''} onChange={set('facility_id')} helperText="Reassign facility">
          {(facilities || []).map((f) => (<MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>))}
        </TextField>
        <FormControlLabel control={<Switch checked={!!form.include_medsmatrix} onChange={set('include_medsmatrix')} />} label="Include MedsMatrix on Reports" />
        <TextField label="Last Login" value={dt(form.last_login)} InputProps={{ readOnly: true }} />
      </Box>
      <Divider />
      <Box p={2} display="flex" gap={1}>
        <Button color="error" startIcon={<DeleteForeverIcon />} onClick={handleDelete}>Delete</Button>
        <Box flexGrow={1} />
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" startIcon={<SaveIcon />} disabled={saving}>{saving ? 'Saving…' : 'Update Provider'}</Button>
      </Box>
    </Drawer>
  );
}

export default function ProvidersPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { search } = useLocation();

  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const qpOrg = qp.get('org') || '';
  const qpFacility = qp.get('facility') || '';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openAdd, setOpenAdd] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');

  const [userId, setUserId] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(qpOrg || null);

  const [facilities, setFacilities] = useState([]);
  const [facilityId, setFacilityId] = useState(qpFacility || '');

  const [reportCounts, setReportCounts] = useState({});

  useEffect(() => { (async () => { const { data, error } = await supabase.auth.getUser(); if (!error && data?.user?.id) setUserId(data.user.id); })(); }, []);

  useEffect(() => {
    (async () => {
      if (!userId) return;
      try {
        const { data: mems } = await supabase.from('organization_members').select('org_id').eq('user_id', userId);
        const ids = (mems || []).map((m) => m.org_id);
        if (!ids.length) { setOrgs([]); setOrgId(null); return; }
        const { data: orgRows } = await supabase.from('organizations').select('id,name').in('id', ids);
        const list = (orgRows || []).map((o) => ({ id: o.id, name: o.name || o.id }));
        setOrgs(list);
        setOrgId((prev) => prev || list[0]?.id || null);
      } catch {}
    })();
  }, [userId]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      if (!orgId) { setFacilities([]); return; }
      try {
        const { data, error } = await supabase.from('facilities').select('id,name').eq('org_id', orgId).order('name', { ascending: true });
        if (error) throw error;
        setFacilities(data || []);
        const url = new URL(window.location.href);
        url.searchParams.set('org', orgId);
        if (facilityId) url.searchParams.set('facility', facilityId); else url.searchParams.delete('facility');
        navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
      } catch {}
    })();
  }, [orgId]); // eslint-disable-line

  const fetchProviders = async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      let q = supabase
        .from('providers')
        .select('id, org_id, facility_id, npi, first_name, last_name, email, phone, fax, address, city, state, zip, country, last_login, include_medsmatrix')
        .eq('org_id', orgId)
        .order('last_name', { ascending: true });
      if (facilityId) q = q.eq('facility_id', facilityId);

      const { data, error } = await q;
      if (error) throw error;

      const mapped = (data || []).map((p) => ({
        id: p.id, org_id: p.org_id, facility_id: p.facility_id,
        npi: p.npi, first_name: p.first_name || '', last_name: p.last_name || '', email: p.email || '',
        phone: p.phone || '', fax: p.fax || '', address: p.address || '', city: p.city || '', state: p.state || '',
        zip: p.zip || '', country: p.country || 'US', last_login: p.last_login || null, include_medsmatrix: !!p.include_medsmatrix,
      }));

      setRows(mapped);

      let rq = supabase.from('lab_reports').select('provider_id').eq('org_id', orgId);
      if (facilityId) rq = rq.eq('facility_id', facilityId);
      const { data: lr } = await rq;
      const counts = {};
      (lr || []).forEach((r) => { if (r.provider_id) counts[r.provider_id] = (counts[r.provider_id] || 0) + 1; });
      setReportCounts(counts);
    } catch (e) {
      alert(`Failed to load providers: ${e.message}`);
      setRows([]); setReportCounts({});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProviders(); /* eslint-disable-line */ }, [orgId, facilityId]);

  const states = useMemo(() => ['all', ...Array.from(new Set(rows.map((r) => r.state).filter(Boolean))).sort()], [rows]);
  const countries = useMemo(() => ['all', ...Array.from(new Set(rows.map((r) => r.country).filter(Boolean))).sort()], [rows]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    return rows.filter((r) => {
      if (stateFilter !== 'all' && r.state !== stateFilter) return false;
      if (countryFilter !== 'all' && r.country !== countryFilter) return false;
      if (!q) return true;
      return [
        r.npi, r.first_name, r.last_name, r.email, r.phone,
        r.address, r.city, r.state, r.zip, r.country,
      ].join(' ').toLowerCase().includes(q);
    });
  }, [rows, searchText, stateFilter, countryFilter]);

  const columns = [
    {
      field: 'menu', headerName: '', width: 60, sortable: false,
      renderCell: (p) => (
        <Box display="flex" alignItems="center">
          <IconButton size="small" onClick={() => { const row = rows.find((r) => r.id === p.row.id); setEditing(row || null); setEditOpen(true); }}>
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton size="small"><MoreVertIcon fontSize="small" /></IconButton>
        </Box>
      ),
    },
    { field: 'npi', headerName: 'NPI', width: 140 },
    { field: 'last_name', headerName: 'Last Name', flex: 1, minWidth: 140 },
    { field: 'first_name', headerName: 'First Name', flex: 1, minWidth: 140 },
    {
      field: 'location', headerName: 'Location', flex: 1.2, minWidth: 180,
      renderCell: ({ row }) => (<Typography variant="body2">{[row.city, row.state].filter(Boolean).join(', ') || '—'}</Typography>),
    },
    { field: 'phone', headerName: 'Phone', width: 150, renderCell: ({ row }) => <Typography>{row.phone || '—'}</Typography> },
    { field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200, renderCell: ({ row }) => <Typography>{row.email || '—'}</Typography> },
    {
      field: 'lab_reports', headerName: 'Lab Reports', width: 130, sortable: false,
      renderCell: ({ row }) => (
        <Button
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            const url = `/lab-results?org=${orgId || ''}&provider=${row.id}${facilityId ? `&facility=${facilityId}` : ''}`;
            navigate(url);
          }}
        >
          {reportCounts[row.id] || 0}
        </Button>
      ),
    },
    { field: 'last_login', headerName: 'Last Login', width: 180, renderCell: ({ row }) => dt(row.last_login) },
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header title="Providers" subtitle="Manage clinicians and contact details" />
        <Box display="flex" gap={1}>
          {orgs.length > 0 && (
            <TextField
              select size="small" label="Organization" value={orgId || ''}
              onChange={(e) => {
                const v = e.target.value || '';
                setOrgId(v); setFacilityId('');
                const url = new URL(window.location.href);
                url.searchParams.set('org', v); url.searchParams.delete('facility');
                navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
              }} sx={{ minWidth: 220 }}
            >
              {orgs.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
            </TextField>
          )}
          {facilities.length > 0 && (
            <TextField
              select size="small" label="Facility" value={facilityId}
              onChange={(e) => {
                const v = e.target.value || '';
                setFacilityId(v);
                const url = new URL(window.location.href);
                url.searchParams.set('org', orgId || '');
                if (v) url.searchParams.set('facility', v); else url.searchParams.delete('facility');
                navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
              }} sx={{ minWidth: 220 }}
            >
              <MenuItem value="">All facilities</MenuItem>
              {facilities.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
            </TextField>
          )}

          <TextField
            size="small" placeholder="Search" value={searchText} onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 280 }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>) }}
          />

          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchProviders} disabled={!orgId}>Refresh</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)} disabled={!orgId}>New</Button>
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField select size="small" fullWidth label="State" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                {['all', ...states.slice(1)].map((s) => (<MenuItem key={s} value={s}>{s === 'all' ? 'All' : s}</MenuItem>))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField select size="small" fullWidth label="Country" value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
                {['all', ...countries.slice(1)].map((c) => (<MenuItem key={c} value={c}>{c === 'all' ? 'All' : c}</MenuItem>))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent>
          <Box sx={{ height: 620, width: '100%' }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              getRowHeight={() => 52}
              sx={{ '& .MuiDataGrid-columnHeaders': { background: theme.palette.background.paper } }}
              initialState={{ pagination: { paginationModel: { pageSize: 15 } } }}
            />
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography variant="caption" color="text.secondary">Providers per page: 15 • {filtered.length} shown</Typography>
          </Box>
        </CardContent>
      </Card>

      <AddProviderDialog open={openAdd} onClose={() => setOpenAdd(false)} onCreated={() => { setOpenAdd(false); fetchProviders(); }} orgId={orgId} facilities={facilities} />
      <EditProviderDrawer open={editOpen} provider={editing} onClose={() => setEditOpen(false)} onSaved={() => fetchProviders()} onDeleted={() => fetchProviders()} facilities={facilities} />
    </Box>
  );
}
